import { memo } from 'react'
import { useField, UseFieldConfig } from 'react-final-form'
import { ITextareaInputProps, TextareaInput } from 'components/extend/TextareaInput'
import { removeNonNumericChars } from 'helpers/common'
import { checkFieldError } from 'helpers/form'

import { useIsEqualWithFormat } from './hooks'

const VALUE_FORMATTER_BY_TYPE: { [key: NonNullable<IProps['type']>]: (value: string) => string } = {
  number: (value: string) => removeNonNumericChars(value)
}

interface IProps
  extends Pick<UseFieldConfig<string>, 'format' | 'formatOnBlur' | 'validate'>,
    Omit<ITextareaInputProps, 'name' | 'value' | 'defaultValue'> {
  name: string
  error?: boolean | string
  onChange?: (value: string) => void
  testId?: string
  type?: string
}

export const FieldTextarea = memo(function FieldTextarea({
  name,
  validate,
  format,
  formatOnBlur,
  error,
  type,
  testId,
  onChange: onChangeFromProps,
  ...inputProps
}: IProps) {
  const isEqual = useIsEqualWithFormat(format)
  const { input, meta } = useField<string>(name, {
    validate,
    format,
    formatOnBlur,
    isEqual,
    parse: (value?: string) => value || ''
  })

  const isNumber = () => type === 'number'

  const formatNumber = (value: string) => {
    if (/^[0-9]+$/.test(value)) {
      return value
    }

    return value.slice(0, -1)
  }

  const handleChange = (value: string) => {
    if (!type) {
      input.onChange(value)
      onChangeFromProps && onChangeFromProps(value)

      return
    }
    if (VALUE_FORMATTER_BY_TYPE[type]) {
      input.onChange(VALUE_FORMATTER_BY_TYPE[type](value))
      onChangeFromProps && onChangeFromProps(VALUE_FORMATTER_BY_TYPE[type](value))

      return
    }
    input.onChange(value)
    onChangeFromProps && onChangeFromProps(value)
  }

  const value = isNumber() ? formatNumber(input.value) : input.value

  return (
    <TextareaInput
      {...inputProps}
      error={error || checkFieldError(meta)}
      name={name}
      onBlur={input.onBlur}
      onChange={handleChange}
      onFocus={input.onFocus}
      testId={testId}
      value={value}
    />
  )
})
